// Netcore Smartech helpers. The SDK is loaded by the plain script in
// public/index.html, which defines window.smartech. If the script is blocked,
// every call here is a no-op.
declare global {
  interface Window {
    smartech?: (...args: any[]) => void;
  }
}

const smartechCall = (...args: any[]): void => {
  if (typeof window === 'undefined' || typeof window.smartech !== 'function') {
    return;
  }
  try {
    window.smartech(...args);
  } catch (error) {
    console.error('Netcore call failed:', error);
  }
};

// The Netcore panel primary key is MOBILE and rejects '+' or any other symbol
// (ERR10013 invalid user id). Identity is digits only: <countrycode><number>.
// A bare 10-digit number is treated as Indian. The Flutter apps must use the
// same format so profiles merge.
export function normalizeMobile(raw?: string | number | null): string {
  const digits = raw ? String(raw).replace(/\D/g, '') : '';
  if (!digits) return '';
  if (digits.length > 10) return digits;
  return `91${digits}`;
}

export function netcoreIdentify(mobile?: string | number | null): void {
  const identity = normalizeMobile(mobile);
  if (!identity) return;
  smartechCall('identify', identity);
}

const pad = (n: number): string => String(n).padStart(2, '0');

// Netcore date/datetime format: yyyy-mm-dd hh:mm:ss (ISO strings are rejected,
// which silently drops the attribute). Date-only input (DOB) gets 00:00:00.
export function netcoreDate(input?: string | number | Date | null): string {
  if (input === undefined || input === null || input === '') return '';
  const dateOnly =
    typeof input === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(input.trim());
  if (dateOnly) return `${input.trim()} 00:00:00`;
  const d = input instanceof Date ? input : new Date(input);
  if (isNaN(d.getTime())) return '';
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
}

// DOB can arrive as yyyy-mm-dd or dd-mm-yyyy / dd/mm/yyyy (Aadhaar KYC).
// Always returns yyyy-mm-dd 00:00:00, or '' if it cannot be parsed.
export function netcoreDob(input?: string | null): string {
  if (!input) return '';
  const s = String(input).trim();
  const dmy = s.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]} 00:00:00`;
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) return `${ymd[1]}-${ymd[2]}-${ymd[3]} 00:00:00`;
  return '';
}

const ISO_DATETIME =/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

// Fields the plan defines as numbers; some APIs return them as strings.
const NUMERIC_KEYS = new Set([
  'value',
  'order_value',
  'order_total',
  'order_grand_total',
  'unit_price',
  'quoted_value',
  'metal_quantity',
  'quantity',
  'bar_size',
  'duration_in_months',
  'LAST_BUY_VALUE',
]);

// Fields the plan defines as strings; IDs and codes can arrive as numbers.
const STRING_KEYS = new Set([
  'mobile',
  'customer_id',
  'account_id',
  'quotation_id',
  'order_no',
  'transaction_id',
  'sip_id',
  'referral_code',
  'coupon_code',
  'gender',
  'income_source',
  'income_group',
  'CUSTOMER_ID',
  'ACCOUNT_ID',
  'GENDER',
  'INCOME_SOURCE',
  'INCOME_GROUP',
]);

// Netcore only accepts string / number / date. Booleans are sent as the
// strings 'true' / 'false', ISO timestamps as yyyy-mm-dd hh:mm:ss, the
// numeric fields above as real numbers and the string fields as strings.
const normalizeValue = (key: string, value: unknown): unknown => {
  if (typeof value === 'boolean') return String(value);
  if (typeof value === 'number' && STRING_KEYS.has(key)) return String(value);
  if (typeof value === 'string') {
    if (ISO_DATETIME.test(value) || DATE_ONLY.test(value)) {
      return netcoreDate(value);
    }
    if (NUMERIC_KEYS.has(key) && value.trim() !== '' && !isNaN(Number(value))) {
      return Number(value);
    }
  }
  return value;
};

// Always sent, as '' when there is no value (a direct signup has no UTM).
const KEEP_EMPTY_KEYS = new Set([
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'UTM_SOURCE',
  'UTM_MEDIUM',
  'UTM_CAMPAIGN',
]);

// Drops undefined/null/empty-string values so missing data is never sent as
// a literal blank attribute (except KEEP_EMPTY_KEYS), and normalises types
// for Netcore.
const clean = (obj: Record<string, unknown>): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    if (KEEP_EMPTY_KEYS.has(key)) {
      out[key] = value === undefined || value === null ? '' : value;
      return;
    }
    if (value !== undefined && value !== null && value !== '') {
      const normalized = normalizeValue(key, value);
      if (normalized !== '') out[key] = normalized;
    }
  });
  return out;
};

export function netcoreDispatch(
  eventName: string,
  payload: Record<string, unknown> = {}
): void {
  smartechCall('dispatch', eventName, clean(payload));
}

// smartech('contact', '1', {...}) updates profile attributes (list id 1).
// Attribute keys are UPPER_CASE; the primary key is 'pk^mobile'.
export function netcoreContact(
  mobile: string | number | null | undefined,
  attributes: Record<string, unknown> = {}
): void {
  const identity = normalizeMobile(mobile);
  if (!identity) return;
  smartechCall('contact', '1', {
    'pk^mobile': identity,
    ...clean(attributes),
  });
}

// CUSTOMER_ID is not known at login time (the customer list loads afterwards),
// so login marks it pending and App.tsx sends it once the customer is selected.
const PENDING_KEY = 'netcoreLoginCustomerIdPending';

export function markLoginCustomerIdPending(): void {
  try {
    sessionStorage.setItem(PENDING_KEY, '1');
  } catch {}
}

export function flushLoginCustomerId(
  mobile?: string | number | null,
  customerId?: string | null
): void {
  if (!customerId || !mobile) return;
  try {
    if (!sessionStorage.getItem(PENDING_KEY)) return;
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    return;
  }
  netcoreContact(mobile, { CUSTOMER_ID: customerId });
}

// Contact update + identify + event, in the order given by the tracking plan.
// Pass attributes = undefined for events that only identify and dispatch.
export function netcoreTrack(
  eventName: string,
  payload: Record<string, unknown>,
  mobile?: string | number | null,
  attributes?: Record<string, unknown>
): void {
  if (attributes) netcoreContact(mobile, attributes);
  netcoreIdentify(mobile);
  netcoreDispatch(eventName, payload);
}

const METAL_NAMES: Record<string, string> = {
  Au: 'Gold',
  Ag: 'Silver',
  Pt: 'Platinum',
  Pd: 'Palladium',
};

export function metalNameFromCode(code?: string | null): string | undefined {
  return code ? METAL_NAMES[code] ?? code : undefined;
}

// First-touch UTM (written once by utils/handleUtmParams.ts). Falls back to the
// latest 'utmData' for visitors who landed before first-touch capture existed.
export function getStoredUtm(): {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
} {
  try {
    const d = JSON.parse(
      localStorage.getItem('netcoreFirstUtm') ||
        localStorage.getItem('utmData') ||
        '{}'
    );
    return {
      utm_source: d?.utmSource || '',
      utm_medium: d?.utmMedium || '',
      utm_campaign: d?.utmCampaign || '',
    };
  } catch {
    return { utm_source: '', utm_medium: '', utm_campaign: '' };
  }
}

export function netcorePageView(): void {
  netcoreDispatch('Page Browse', { page_url: window.location.href });
}
